import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import type { ColumnDef } from '@tanstack/react-table';

import { formatPrice } from 'utils/price';
import { useGetBasketQuery, TransactionState } from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { PAGES } from 'constants/pages';
import { Loader } from 'components/Loader';
import { SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { CopyValue } from 'components/CopyValue';
import { DetailLink, DetailValue } from 'components/DetailsView';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { TotemLabel } from 'components/TotemLabel';
import { DataTable, dateColumn, textColumn } from 'components/DataTable';
import { StatusTag } from 'pages/Transactions/components/StatusTag';

type ParamTypes = {
    basketId: string;
};

type TransactionRow = {
    _id: string;
    createdAt: number;
    siteName: string;
    newState: TransactionState;
    productName: string;
    priceHT: number;
    tva: number;
};

const transactionColumns: ColumnDef<TransactionRow, unknown>[] = [
    dateColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'createdAt',
        header: 'Date',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'newState',
        header: 'Statut',
        defaultVisible: true,
        cell: ({ row }) => <StatusTag state={row.original.newState} />,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'siteName',
        header: 'Site',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'productName',
        header: 'Produit',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        id: 'price',
        accessorFn: (row) => formatPrice(row.priceHT * (1 + row.tva)),
        header: 'Montant',
        defaultVisible: true,
    }),
];

export const Basket = () => {
    const { basketId = '' } = useParams<ParamTypes>();

    const { data: basketData, loading, error } = useGetBasketQuery({ variables: { basketId } });

    if (loading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (error || !basketData?.basket) {
        throw new Error('Une erreur est survenue lors de la récupération du panier');
    }

    const {
        _id,
        type,
        externalId,
        externalMachineId,
        cardId,
        userId,
        siteId,
        purchasedAt,
        createdAt,
        sensei,
        transactions,
    } = basketData.basket;

    const { userFullName, siteName } = transactions[0] ?? { userFullName: 'inconnu', siteName: 'inconnu' };

    const sortedTransactions = [...transactions].sort(
        (transactionA, transactionB) => transactionB.createdAt - transactionA.createdAt,
    );

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.basket} />
                </HeaderTitle>
            </Header>
            <Content>
                <ScrollableContent>
                    <SectionContainer title="Informations générales" keepOpen>
                        <SectionColumn>
                            <CopyValue label="ID du panier" value={_id} />
                            <DetailValue label="Fournisseur" value={type} />
                            <CopyValue label="Identifiant chez le fournisseur" value={externalId} />
                            <DetailValue label="Machine du fournisseur" value={externalMachineId ?? 'inconnue'} />
                            <DetailLink label="Utilisateur" name={userFullName} value={userId} path="user" />
                            {siteId ? <DetailLink label="Site" name={siteName} value={siteId} path="site" /> : null}
                            {cardId ? <DetailValue label="Carte" value={cardId} /> : null}
                            <DetailValue label="Date de la vente" value={getExtendedFormattedDatetime(purchasedAt)} />
                            <DetailValue label="Réception par Totem" value={getExtendedFormattedDatetime(createdAt)} />
                            {sensei ? (
                                <>
                                    <DetailValue
                                        label="Ouverture de la porte"
                                        value={getExtendedFormattedDatetime(sensei.storeEntryDate)}
                                    />
                                    <DetailValue
                                        label="Fermeture de la porte"
                                        value={getExtendedFormattedDatetime(sensei.storeExitDate)}
                                    />
                                    <DetailValue
                                        label="Création du panier - Sensei"
                                        value={getExtendedFormattedDatetime(sensei.basketCreationDate)}
                                    />
                                    <TotemLabel>Produits détectés</TotemLabel>
                                    <TableContainer>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>sku (productId)</th>
                                                    <th>quantité</th>
                                                    <th>montant</th>
                                                    <th>taxes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sensei.items.map(({ quantity, sku, lineAmount, lineTax }, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <DetailLink
                                                                name={
                                                                    (sku &&
                                                                        transactions.find(
                                                                            ({ productId }) => productId === sku,
                                                                        )?.productName) ||
                                                                    sku ||
                                                                    'produit inconnu'
                                                                }
                                                                value={sku}
                                                                path="product"
                                                            />
                                                        </td>
                                                        <td>{quantity}</td>
                                                        <td>{lineAmount}</td>
                                                        <td>{lineTax}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </TableContainer>
                                </>
                            ) : null}
                            <TotemLabel>Transactions</TotemLabel>
                            <DataTable<TransactionRow>
                                columns={transactionColumns}
                                data={sortedTransactions}
                                filterMode="client"
                                getRowId={(row) => row._id}
                                getRowLink={(row) => `/transaction/${row._id}`}
                                autoFocusSearch={false}
                            />
                        </SectionColumn>
                    </SectionContainer>
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 5px;
    }

    table {
        border-spacing: 0;
        border-collapse: collapse;
        color: ${({ theme }) => theme.textColor};

        th,
        td {
            text-align: left;
            border: 1px solid ${({ theme }) => theme.lightBorderColor};
            border-spacing: 1px;
        }

        th {
            padding: 10px;
        }

        td {
            padding: 5px;
        }
    }
`;
